import AASplashPage from "./views/assistant/AASplashPage.tsx";
import AssistantPersonaRedirect from "./views/assistant/components/AssistantPersonaRedirect";
import { AssistantTemplates } from "./views/assistant/AssistantTemplates";
import { AtomAssistActions } from "./views/assistant/actions/AtomAssistActions.tsx";
import FfnSplashPage from "./views/products/fifty-fifty-nine/FfnSplashPage.tsx";
import ItemEquivalencyChatPage from "./views/products/item-equivalency/ItemEquivalencyChatPage.tsx";
import ItemEquivalencyRunsPage from "./views/products/item-equivalency/ItemEquivalencyRunsPage.tsx";
import FfnChatPage from "./views/products/fifty-fifty-nine/FfnChatPage.tsx";
import FfnRecentsPage from "./views/products/fifty-fifty-nine/FfnRecentsPage.tsx";
import FfnRecordsDashboardPage from "./views/products/fifty-fifty-nine/FfnRecordsDashboardPage.tsx";
import VerificationsPage from "./views/assistant/verifications/VerificationsPage.tsx";
import AtomAssistChatPage from "./views/assistant/AtomAssistChatPage.tsx";
import PartsAIChatPage from "./views/products/parts-ai/PartsAIChatPage";
import PartsAISplashPage from "./views/products/parts-ai/PartsAISplashPage.tsx";
import PartsAISearchSplashPage from "./views/products/parts-ai/PartsAISearchSplashPage.tsx";
import PartsAIComparisonSplashPage from "./views/products/parts-ai/PartsAIComparisonSplashPage.tsx";
import PartsAIRecentsPage from "./views/products/parts-ai/PartsAIRecentsPage.tsx";
import PartsAIHomePage from "./views/products/parts-ai/PartsAIHomePage.tsx";
import PartsDashboardPage from "./views/products/parts-ai/dashboard/PartsDashboardPage.tsx";
import PartDetailView from "./views/products/parts-ai/dashboard/PartDetailView.tsx";
import PartSchemaEditorPage from "./views/products/parts-ai/dashboard/PartSchemaEditorPage.tsx";
import ProposalGenerationChatPage from "./views/products/proposal-generation/ProposalGenerationChatPage.tsx";
import ProposalGenerationSplashPage from "./views/products/proposal-generation/ProposalGenerationSplashPage.tsx";
import ProposalGenerationRecentsPage from "./views/products/proposal-generation/ProposalGenerationRecentsPage.tsx";
import ProposalGenerationRunsPage from "./views/products/proposal-generation/ProposalGenerationRunsPage.tsx";
import VnvAssistSplashPage from "./views/products/vnv-assist/VnvAssistSplashPage.tsx";
import VnvAssistChatPage from "./views/products/vnv-assist/VnvAssistChatPage.tsx";
import VnvAssistRecentsPage from "./views/products/vnv-assist/VnvAssistRecentsPage.tsx";
import { AutomationConfigTemplateDetail } from "./views/products/cap-ai/cap-automation/automation-landing/CapAutoLanding";
import { CAPAutomationRecords } from "./views/products/cap-ai/cap-automation/automation-records/AutomationRecords";
import { AdvancedCapScreeningBeta } from "./views/products/cap-ai/cap-automation/beta/AdvancedCapScreeningBeta";
import { CAPAutomationStats } from "./views/products/cap-ai/cap-automation/automation-kpis/AutomationKPIs";
import DatasetScoring from "./views/datasetScoring/DatasetScoringList";
import JobsRun from "./views/jobs/JobsRun";
import { ProjectOverviewProvider } from "./views/products/work-management-ai/project-overview/ProjectOverviewProvider";
import React from "react";
import { Redirect } from "react-router-dom";
import { useGetActivityDetails } from "./views/products/obervation-program/ObservationProgramServices";
import { FeatureFlag } from "./config/featureFlags";
import {
  withSmartCrAdminGate,
  withSmartCrGate,
} from "./views/products/cap-ai/SmartCrGate";
import { withObservationInterviewGate } from "./views/products/obervation-program/ObservationProgramGate";
import {
  EQUIPMENT_AI_ROUTES,
  EQUIPMENT_AI_INTEGRATIONS_PATH,
  EQUIPMENT_AI_SCHEDULES_PATH,
  onboardingUrl,
} from "./views/products/equipment-ai/utils/equipmentAiPaths";

const User = React.lazy(() => import("./views/users/User"));

const UsersPage = React.lazy(() => import("./views/users/UsersPage"));

const GroupManagePage = React.lazy(
  () => import("./views/admin/groups/GroupManagePage"),
);

// /users/groups without a group id is only reachable via the breadcrumb that
// CBreadcrumbRouter auto-generates on /users/groups/:groupId. Without an
// explicit route it falls through to /users/:id (id="groups") and renders a
// broken user-details page. Land on the Groups tab of Manage Users instead.
const UsersGroupsRedirect = () => <Redirect to="/users?tab=groups" />;
const CrLifecycleRedirect = () => (
  <Redirect to="/products/cap-ai/cr/configuration?tab=rules" />
);

const Buckets = React.lazy(() => import("./views/buckets/Buckets"));

const TimeSeriesSources = React.lazy(
  () => import("./views/time-series/TimeSeriesSources"),
);
const TimeSeriesSourceDetail = React.lazy(
  () => import("./views/time-series/TimeSeriesSourceDetail"),
);

const Reports = React.lazy(() => import("./views/reports/Reports"));

const ChatObservationPage = React.lazy(
  () => import("./views/products/obervation-program/ChatObservationPage.tsx"),
);
const FolderBrowser = React.lazy(
  () => import("./views/assistant/projects/FolderBrowser.tsx"),
);

const WorkspacePage = React.lazy(
  () => import("./views/assistant/workspace/WorkspacePage.tsx"),
);

const AssistantTemplatesProvider = (props) => {
  return <AssistantTemplates {...props} />;
};

const AtomAssistPersonasPage = React.lazy(
  () => import("./views/assistant/AtomAssistPersonasPage.tsx"),
);

const AtomAssistRecentsPage = React.lazy(
  () => import("./views/assistant/AtomAssistRecentsPage.tsx"),
);

const AdminAgentJobs = React.lazy(
  () => import("./views/agentJobs/AdminAgentJobs.tsx"),
);
const AdminAgentSessions = React.lazy(
  () => import("./views/agentSessions/AdminAgentSessions.tsx"),
);
const AdminEvalDashboard = React.lazy(
  () => import("./views/evalDashboard/AdminEvalDashboard.tsx"),
);
const EdgeConnectorsPage = React.lazy(
  () => import("./views/edgeConnectors/EdgeConnectorsPage.tsx"),
);

// Demo pages (development only)
const DesignSystemDemo = React.lazy(
  () => import("./views/demo/DesignSystemDemo.tsx"),
);
const DatasetsDemo = React.lazy(() => import("./views/demo/DatasetsDemo.tsx"));
const ModelControlDemo = React.lazy(
  () => import("./views/demo/ModelControlDemo.tsx"),
);
const SearchEvalPage = React.lazy(
  () => import("./views/developer/SearchEvalPage.tsx"),
);
const TrainingExportPage = React.lazy(
  () => import("./views/developer/TrainingExportPage.tsx"),
);
const RolloutPage = React.lazy(
  () => import("./views/developer/RolloutPage.tsx"),
);

const ReportViewer = React.lazy(() => import("./views/reports/ReportViewer"));
const RecordViewPage = React.lazy(
  () => import("./features/library/record/RecordViewPage"),
);

const Datasets = React.lazy(() => import("./views/datasets/Datasets"));
const Jobs = React.lazy(() => import("./views/jobs/Jobs"));
const JobsCreateAndUpdate = React.lazy(
  () => import("./views/jobs/JobsCreateAndUpdate"),
);
const JobSchedules = React.lazy(() => import("./views/jobs/JobSchedules"));
const JobsViewer = React.lazy(() => import("./views/jobs/JobsViewer"));
const DatasetViewer = React.lazy(
  () => import("./views/datasets/datasetViewer/DatasetViewer.tsx"),
);
const DatasetModelStats = React.lazy(
  () => import("./views/datasets/DatasetModelStats"),
);
const UploadToDataset = React.lazy(
  () => import("./views/datasets/UploadToDataset"),
);
const DatasetPredictions = React.lazy(
  () => import("./views/datasets/DatasetPredictions"),
);

const ModelList = React.lazy(() => import("./views/models/Models"));
//const TaskList = React.lazy(() => import("./views/tasks/Tasks"));
const ProductList = React.lazy(() => import("./views/products/Products"));
const OpDashboard = React.lazy(
  () => import("./views/products/op-dashboard/OpDashboard"),
);
const CapAI = React.lazy(() => import("./views/products/cap-ai/CapAI"));
const CapCrHome = React.lazy(
  () => import("./views/products/cap-ai/cr-generation/CapCrHome"),
);
const CrDraftBox = React.lazy(
  () => import("./views/products/cap-ai/cr-generation/CrDraftBox"),
);
const CrIntake = React.lazy(
  () => import("./views/products/cap-ai/cr-generation/CrIntake"),
);
const CrRecordsBrowser = React.lazy(
  () => import("./views/products/cap-ai/cr-records/CrRecordsBrowser"),
);
const CrRecordDetail = React.lazy(
  () => import("./views/products/cap-ai/cr-records/CrRecordDetail"),
);
const CrConfiguration = React.lazy(
  () => import("./views/products/cap-ai/cr-generation/CrConfiguration"),
);
const CrLifecyclePage = React.lazy(
  () => import("./views/products/cap-ai/cr-records/CrLifecycleAdmin"),
);
const Capitalizer = React.lazy(
  () => import("./views/products/capitalizer/Capitalizer"),
);
const AutomationConfigTemplateList = React.lazy(
  () =>
    import("./views/products/cap-ai/cap-automation/AutomationConfigTemplateList"),
);
const AutomationConfig = React.lazy(
  () => import("./views/products/cap-ai/cap-automation/CapAuto"),
);

const SafetyAnalytics = React.lazy(
  () => import("./views/products/safety-analytics/SafetyAnalytics"),
);
const RegulatoryAI = React.lazy(
  () => import("./views/products/regulatory-ai/RegulatoryAI"),
);
const ModelDetails = React.lazy(() => import("./views/models/ModelDetail"));

const UserProfile = React.lazy(() => import("./views/users/UserProfile"));

const ManagementConsole = React.lazy(
  () => import("./views/managementConsole/ManagementConsole"),
);

const ResourceManagement = React.lazy(
  () => import("./views/resourceManagement/ResourceManagementPage"),
);

const HubSettings = React.lazy(() => import("./views/hubSettings/HubSettings"));

const TrendCodingProductPatch = React.lazy(() =>
  import("./views/products/cap-ai/cap-trending/trendingDashboard").then(
    (module) => ({ default: module.TrendCodingProductPatch }),
  ),
);
const AutomatedTrendCodingProductPatch = React.lazy(() =>
  import("./views/products/cap-ai/cap-trending/trendingDashboard").then(
    (module) => ({ default: module.AutomatedTrendCodingProductPatch }),
  ),
);
const IntegrateAutomation = React.lazy(
  () =>
    import("./views/products/cap-ai/cap-automation/automation-integration/IntegrateAutomation"),
);

const PortfolioDashboard = React.lazy(
  () => import("./views/products/work-management-ai/PortfolioDashboard"),
);

const ProjectOverview = React.lazy(
  () =>
    import("./views/products/work-management-ai/project-overview/ProjectOverview"),
);

const PersonaMarketplace = React.lazy(
  () => import("src/views/products/persona-marketplace/PersonaMarketplace"),
);

const PersonaDetails = React.lazy(
  () =>
    import("src/views/products/persona-marketplace/PersonaDetails/PersonaDetailsPage"),
);

const DatasetMarketplace = React.lazy(
  () => import("src/views/products/dataset-marketplace/DatasetMarketplace"),
);

const ProjectOverviewWithProvider = (props) => {
  return (
    <ProjectOverviewProvider>
      <ProjectOverview {...props} />
    </ProjectOverviewProvider>
  );
};

const ObservationProgram = React.lazy(
  () => import("./views/products/obervation-program/ObservationProgram"),
);

// const ObservationProgramAdmin = React.lazy(() =>
//   import("./views/products/obervation-program/ObservationProgramAdmin")
// );

const OAuth = React.lazy(() => import("./views/oauth/OAuth"));

const OAuthConfigure = React.lazy(() => import("./views/oauth/OAuthConfigure"));

const CaseObservation = React.lazy(
  () => import("./views/products/obervation-program/CaseObservation"),
);
const Activity = React.lazy(
  () => import("./views/products/obervation-program/Activities"),
);

// have to import as default export to fix React.lazy error
const KeyValueComponent = React.lazy(() =>
  import("./reusable/components/KeyValueComponent").then((module) => ({
    default: module.KeyValueComponent,
  })),
);

const CriteriaOverview = React.lazy(
  () => import("./views/products/obervation-program/CriteriaOverview"),
);

const AtomResultsPageView = React.lazy(
  () => import("./views/atomResults/AtomResultsPageView.tsx"),
);

const EmailRecordsPage = React.lazy(
  () => import("./views/emailRecords/EmailRecordsPage"),
);

// Equipment AI
const EquipmentAI = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentAI"),
);
const EquipmentChatRedirect = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentChatRedirect"),
);
const EquipmentLegacyRedirect = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentLegacyRedirect"),
);
const EquipmentAIRecentsPage = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentAIRecentsPage"),
);
const EquipmentAIIntegrations = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentAIIntegrations"),
);
const EquipmentAISchedulesPage = React.lazy(
  () => import("./views/products/equipment-ai/EquipmentAISchedulesPage"),
);
const EquipmentAIOnboarding = React.lazy(
  () => import("./views/products/equipment-ai/components/OnboardingPage"),
);

// Document Intelligence
const DocumentIntelligence = React.lazy(
  () => import("./views/products/document-intelligence/DocumentIntelligence"),
);
const TemplateList = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/TemplateList"),
);
const TemplateDetail = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/TemplateDetail"),
);
const TemplateForm = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/TemplateForm"),
);
const FieldExtraction = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/FieldExtraction"),
);
const ExtractionDetail = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/ExtractionDetail"),
);
const ExtractionsList = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/ExtractionsList"),
);
const TemplateSchemaReview = React.lazy(
  () =>
    import("./views/products/document-intelligence/components/TemplateSchemaReview"),
);

const routes = [
  { path: "/", exact: true, name: "Home" },
  {
    path: "/datasets/:dataset_id/predictions/:source_uid",
    exact: true,
    name: "Dataset Predictions",
    component: DatasetPredictions,
  },
  {
    // The dataset-homed record view (#2378, design spec §6.3; canonicalized on
    // the dataset-scoped path so every record's URL says which dataset it's
    // in — :source_uid has always been the SDR id, not a source-system uid).
    // A folder-homed record's URL is the separate PROJECT_FILE_ROUTE_PATH
    // (src/views/assistant/utils/projectFileRoute.ts) instead.
    path: "/datasets/:dataset_id/document/:source_uid",
    exact: true,
    name: "Document Viewer",
    component: RecordViewPage,
  },
  {
    path: "/datasets/:dataset_id/stats",
    exact: true,
    name: "Dataset Analytics",
    component: DatasetModelStats,
  },
  {
    path: "/datasets/:dataset_id/viewer",
    exact: true,
    name: "Dataset Viewer",
    component: DatasetViewer,
  },
  {
    path: "/datasets/dataset-folders/:folder_id",
    exact: true,
    name: "Dataset Folder Viewer",
    component: Datasets,
  },
  {
    path: "/datasets/dataset-folders/:folder_id/dataset/:dataset_id/viewer",
    exact: true,
    name: "Dataset Viewer",
    component: DatasetViewer,
  },
  {
    path: "/datasets/:dataset_id/upload",
    exact: true,
    name: "Upload Data",
    component: UploadToDataset,
  },
  { path: "/datasets", exact: true, name: "Datasets", component: Datasets },
  {
    path: "/atom-results",
    exact: true,
    name: "AtomResults",
    component: AtomResultsPageView,
  },
  {
    path: "/email-records",
    exact: true,
    name: "Email Records",
    component: EmailRecordsPage,
  },
  {
    path: "/jobs/create",
    exact: true,
    name: "Create Job",
    component: JobsCreateAndUpdate,
  },
  {
    path: "/jobs/:job_id/update",
    exact: true,
    name: "Update Job",
    component: JobsCreateAndUpdate,
  },
  {
    path: "/jobs/:job_id/run",
    exact: true,
    name: "Run Job",
    component: JobsRun,
  },
  {
    path: "/jobs/:job_id/job-schedule",
    exact: true,
    name: "Job Schedules",
    component: JobSchedules,
  },
  {
    path: "/jobs/:job_id/viewer",
    exact: true,
    name: "Job Runs",
    component: JobsViewer,
  },
  {
    path: "/jobs",
    exact: true,
    name: "Jobs",
    component: Jobs,
  },
  { path: "/buckets", exact: true, name: "Buckets", component: Buckets },
  { path: "/reports", exact: true, name: "Reports", component: Reports },
  {
    path: "/assistant",
    exact: true,
    name: "Assistant",
    component: AASplashPage,
  },
  {
    path: "/assistant/chat/:dialogueId",
    exact: true,
    name: "Assistant",
    component: AtomAssistChatPage,
  },
  {
    path: "/assistant/recents",
    exact: true,
    name: "Chat History",
    component: AtomAssistRecentsPage,
  },
  {
    path: "/parts/chat",
    exact: true,
    name: "Chat",
    component: PartsAISplashPage,
  },
  {
    path: "/parts/search",
    exact: true,
    name: "Search",
    component: PartsAISearchSplashPage,
  },
  {
    path: "/parts/compare",
    exact: true,
    name: "Comparison",
    component: PartsAIComparisonSplashPage,
  },
  {
    path: "/parts/compare/runs",
    exact: true,
    name: "Item Equivalency Runs",
    component: ItemEquivalencyRunsPage,
    _roles: "product:read",
    _product: "parts-ai",
  },
  {
    path: "/parts/chat/search/:dialogueId",
    exact: true,
    name: "Search",
    component: PartsAIChatPage,
  },
  {
    // Comparison IS Item Equivalency now: old deep links and recents keep
    // resolving, into the same chat page as /item-equivalency/chat.
    path: "/parts/chat/comparison/:dialogueId",
    exact: true,
    name: "Comparison",
    component: ItemEquivalencyChatPage,
  },
  {
    path: "/parts/chat/:parts_ai_run_id/:dialogueId",
    exact: true,
    name: "Chat",
    component: PartsAIChatPage,
  },
  {
    path: "/parts/recents",
    exact: true,
    name: "Chat History",
    component: PartsAIRecentsPage,
  },
  {
    path: "/parts/schemas",
    exact: true,
    name: "Part Schemas",
    component: PartSchemaEditorPage,
  },
  {
    path: "/parts",
    exact: true,
    name: "Parts AI",
    component: PartsAIHomePage,
  },
  {
    path: "/parts/catalog",
    exact: true,
    name: "Parts Catalog",
    component: PartsDashboardPage,
  },
  {
    path: "/parts/detail/:partId",
    exact: true,
    name: "Part Detail",
    component: PartDetailView,
  },
  {
    path: "/proposal-generation",
    exact: true,
    name: "Proposal Generation",
    component: ProposalGenerationSplashPage,
    _roles: "product:read",
    _product: "proposal-generation",
  },
  {
    path: "/proposal-generation/chat/:proposal_generation_run_id/:dialogueId",
    exact: true,
    name: "Proposal Generation",
    component: ProposalGenerationChatPage,
    _roles: "product:read",
    _product: "proposal-generation",
  },
  {
    path: "/proposal-generation/recents",
    exact: true,
    name: "Proposal Generation History",
    component: ProposalGenerationRecentsPage,
    _roles: "product:read",
    _product: "proposal-generation",
  },
  {
    path: "/proposal-generation/runs",
    exact: true,
    name: "Proposal Generation Runs",
    component: ProposalGenerationRunsPage,
    _roles: "product:read",
    _product: "proposal-generation",
  },
  {
    path: "/vnv-assist",
    exact: true,
    name: "VNV Assist",
    component: VnvAssistSplashPage,
  },
  {
    path: "/vnv-assist/chat/:vnv_run_id/:dialogueId",
    exact: true,
    name: "VNV Assist",
    component: VnvAssistChatPage,
  },
  {
    path: "/vnv-assist/recents",
    exact: true,
    name: "VNV Assist History",
    component: VnvAssistRecentsPage,
  },
  {
    // Single Route with an array of paths so navigation between the
    // forest view and a folder view does not unmount FolderBrowser —
    // unmounting it would tear down the layout (including the folder
    // tree) on every click. React Router v5 supports `path: string[]`.
    path: [
      "/assistant/projects/folder/:folder_id/chat/:dialogue_id",
      // Deep link to one file by SDR id (#2281): FolderBrowser reflects it
      // into the viewer store, so citation links reach the cited document.
      "/assistant/projects/folder/:folder_id/file/:sdr_id",
      "/assistant/projects/folder/:folder_id",
      // Browse one collection in the center stage (#2379).
      "/assistant/projects/collection/:collection_id",
      "/assistant/projects",
    ],
    exact: true,
    name: "Projects",
    component: FolderBrowser,
  },
  {
    path: "/assistant/workspace",
    exact: true,
    name: "Workspace",
    component: WorkspacePage,
  },
  {
    path: "/assistant/actions",
    exact: true,
    name: "Actions",
    component: AtomAssistActions,
    _roles: "admin",
  },
  {
    // Kept so VerificationsPage cross-links and old bookmarks keep working;
    // the canonical path is /fifty-fifty-nine/records below.
    path: "/assistant/records",
    exact: true,
    name: "50.59 Records",
    component: FfnRecordsDashboardPage,
    _roles: "product:read",
    _product: "licensing-compliance",
    _internalOnly: true,
  },
  {
    path: "/fifty-fifty-nine/records",
    exact: true,
    name: "50.59 Records",
    component: FfnRecordsDashboardPage,
    _roles: "product:read",
    _product: "licensing-compliance",
    _internalOnly: true,
  },
  {
    path: "/fifty-fifty-nine",
    exact: true,
    name: "50.59 Evaluation",
    component: FfnSplashPage,
    _roles: "product:read",
    _product: "licensing-compliance",
  },
  {
    // The evaluation entry point is /parts/compare; only the chat itself
    // (deep-linked from recents/runs and in-flight dialogues) lives here.
    path: "/item-equivalency/chat/:dialogueId",
    exact: true,
    name: "Item Equivalency",
    component: ItemEquivalencyChatPage,
    _roles: "product:read",
    _product: "parts-ai",
  },
  {
    path: "/fifty-fifty-nine/chat/:dialogueId",
    exact: true,
    name: "50.59 Evaluation",
    component: FfnChatPage,
    _roles: "product:read",
    _product: "licensing-compliance",
  },
  {
    path: "/fifty-fifty-nine/recents",
    exact: true,
    name: "50.59 History",
    component: FfnRecentsPage,
    _roles: "product:read",
    _product: "licensing-compliance",
  },
  {
    path: "/assistant/verifications",
    exact: true,
    name: "Verifications",
    component: VerificationsPage,
    _roles: "product:read",
    _product: "licensing-compliance",
    _internalOnly: true,
  },
  {
    path: "/assistant/actions/:action_id",
    exact: true,
    name: "Actions",
    component: AtomAssistActions,
    _roles: "admin",
  },
  {
    path: "/assistant/configs",
    exact: true,
    name: "Agents",
    component: AtomAssistPersonasPage,
  },
  {
    path: "/assistant/persona-marketplace",
    exact: true,
    name: "Agent Marketplace",
    component: PersonaMarketplace,
    _roles: "admin",
  },
  {
    path: "/assistant/persona-marketplace/persona/:personaSlug/resource/:resourceId",
    exact: true,
    name: "Agent Details",
    component: PersonaDetails,
  },
  {
    path: "/datasets/dataset-marketplace",
    exact: true,
    name: "Dataset Marketplace",
    component: DatasetMarketplace,
  },
  {
    // NOTE: this has to be kept here to redirect to the correct route
    path: "/assistant/config/:slug_name",
    exact: true,
    name: "Agents",
    component: AssistantPersonaRedirect,
  },
  {
    path: "/assistant/templates",
    exact: true,
    name: "Templates",
    component: AssistantTemplatesProvider,
    _roles: "nuclearn:write",
    _product: "atom-assist",
  },
  {
    path: "/assistant/templates/datasets/:dataset_id/viewer",
    exact: true,
    name: "Template Dataset",
    component: DatasetViewer,
    _roles: "nuclearn:write",
    _product: "atom-assist",
  },
  {
    path: "/assistant/direct-tools",
    exact: true,
    name: "Direct Tools",
    component: React.lazy(
      () => import("./views/assistant/directTools/DirectToolsPage"),
    ),
  },
  {
    path: "/reports/:report_id",
    exact: true,
    name: "Report Viewer",
    component: ReportViewer,
  },
  {
    path: "/models",
    exact: true,
    name: "Models",
    component: ModelList,
    _roles: "admin",
  },
  {
    path: "/modeldetails/:model_id",
    exact: true,
    name: "Models",
    component: ModelDetails,
  },

  { path: "/products", exact: true, name: "Products", component: ProductList },
  {
    path: "/products/op-dashboard",
    exact: true,
    name: "OP Dashboard",
    component: OpDashboard,
    _roles: "admin",
  },
  { path: "/products/cap-ai", exact: true, name: "CAP AI", component: CapAI },
  {
    path: "/products/cap-ai/cr",
    exact: true,
    name: "CAP AI Condition Reports",
    component: withSmartCrGate(CapCrHome),
  },
  {
    path: "/products/cap-ai/cr/drafts",
    exact: true,
    name: "My CR Drafts",
    component: withSmartCrGate(CrDraftBox),
  },
  {
    path: "/products/cap-ai/cr/draft/:draftId",
    exact: true,
    name: "New Condition Report",
    component: withSmartCrGate(CrIntake),
  },
  {
    path: "/products/cap-ai/cr/records",
    exact: true,
    name: "CAP Records",
    component: withSmartCrGate(CrRecordsBrowser),
  },
  {
    path: "/products/cap-ai/cr/lifecycle",
    exact: true,
    name: "CR Lifecycle & Rules",
    component: withSmartCrGate(CrLifecyclePage),
  },
  {
    path: "/products/cap-ai/cr/records/:recordId",
    exact: true,
    name: "Condition Report",
    component: withSmartCrGate(CrRecordDetail),
  },
  {
    path: "/products/cap-ai/cr/configuration",
    exact: true,
    name: "CR Configuration",
    component: withSmartCrAdminGate(CrConfiguration),
  },
  {
    path: "/products/cap-ai/cr/lifecycle-admin",
    exact: true,
    name: "CR Lifecycle & Rules",
    component: withSmartCrAdminGate(CrLifecycleRedirect),
  },
  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id/flow",
    exact: true,
    name: "AutoFlow",
    component: AutomationConfig,
  },

  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id/integrate",
    exact: true,
    name: "Integrate Automation",
    component: IntegrateAutomation,
  },
  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id/automation-records",
    exact: true,
    name: "Automation Records",
    component: CAPAutomationRecords,
  },
  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id/automation-kpis",
    exact: true,
    name: "Automation KPIs",
    component: CAPAutomationStats,
  },
  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id/beta",
    exact: true,
    name: "Advanced CAP Screening (Beta)",
    component: AdvancedCapScreeningBeta,
  },
  {
    path: "/products/cap-ai/auto/act/:automation_config_template_id",
    exact: true,
    name: "Automation Template",
    component: AutomationConfigTemplateDetail,
  },
  {
    path: "/products/cap-ai/auto",
    exact: true,
    name: "Automation",
    component: AutomationConfigTemplateList,
  },
  {
    path: "/products/cap-ai/cap-trending-dashboard",
    exact: true,
    name: "CAP Trending Dashboard",
    component: TrendCodingProductPatch,
  },
  {
    path: "/products/cap-ai/automated-trend-coding",
    exact: true,
    name: "Automated Trend Coding",
    component: AutomatedTrendCodingProductPatch,
  },
  {
    path: "/products/capitalizer",
    exact: true,
    name: "Capitalizer",
    component: Capitalizer,
  },
  {
    path: "/products/project-genius",
    exact: true,
    name: "Project Genius",
    component: PortfolioDashboard,
  },
  {
    path: "/products/project-genius/portfolio",
    exact: true,
    name: "Portfolio",
    component: PortfolioDashboard,
  },
  {
    path: "/products/project-genius/portfolio/project-overview/:project_id",
    exact: true,
    name: "Project Overview",
    component: ProjectOverviewWithProvider,
  },
  {
    path: "/products/scoring-wizard",
    exact: true,
    name: "Scoring Wizard",
    component: DatasetScoring,
  },
  {
    path: "/products/project-genius-wizard",
    exact: true,
    name: "Project Genius Dashboard",
    component: () => {
      return <PortfolioDashboard isModalVisibleProp={true} />;
    },
  },
  {
    path: "/products/safety-analytics",
    exact: true,
    name: "Safety Analytics",
    component: SafetyAnalytics,
  },
  {
    path: "/products/regulatory-ai",
    exact: true,
    name: "Regulatory AI",
    component: RegulatoryAI,
  },
  // Document Intelligence routes
  {
    path: "/document-intelligence",
    exact: true,
    name: "Document Intelligence",
    component: DocumentIntelligence,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/templates",
    exact: true,
    name: "Form Templates",
    component: TemplateList,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/templates/new",
    exact: true,
    name: "New Template",
    component: TemplateForm,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/templates/:id",
    exact: true,
    name: "Template Details",
    component: TemplateDetail,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/templates/:id/edit",
    exact: true,
    name: "Edit Template",
    component: () => <TemplateForm isEdit />,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/templates/:id/review",
    exact: true,
    name: "Review Template Schema",
    component: TemplateSchemaReview,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/extract-fields",
    exact: true,
    name: "Field Extraction",
    component: FieldExtraction,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/extractions",
    exact: true,
    name: "Extractions List",
    component: ExtractionsList,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/document-intelligence/extractions/:id",
    exact: true,
    name: "Extraction Detail",
    component: ExtractionDetail,
    featureFlag: FeatureFlag.DOCUMENT_INTELLIGENCE,
  },
  {
    path: "/users",
    exact: true,
    name: "Users",
    component: UsersPage,
    _roles: "admin",
  },
  {
    // Must precede /users/:id — otherwise that route captures /users/groups
    // as a user with id "groups".
    path: "/users/groups",
    exact: true,
    name: "Groups",
    component: UsersGroupsRedirect,
    _roles: "admin",
  },
  {
    path: "/users/:id",
    exact: true,
    name: "User Details",
    component: User,
  },
  {
    path: "/users/groups/:groupId",
    exact: true,
    name: "Manage Group",
    component: GroupManagePage,
    _roles: "admin",
  },
  {
    path: "/userprofile",
    exact: true,
    name: "User Profile",
    component: UserProfile,
  },
  {
    path: "/management-console",
    exact: true,
    name: "Management Console",
    component: ManagementConsole,
    _roles: "admin",
  },
  {
    path: "/resource-management",
    exact: true,
    name: "Resource Management",
    component: ResourceManagement,
    _roles: "admin",
  },
  {
    path: "/hub-settings",
    exact: true,
    name: "Hub Settings",
    component: HubSettings,
    _roles: "admin",
  },
  {
    path: "/admin/agent-jobs",
    exact: true,
    name: "Agent Jobs",
    component: AdminAgentJobs,
    _roles: "admin",
  },
  {
    path: "/admin/agent-sessions",
    exact: true,
    name: "Agent Sessions",
    component: AdminAgentSessions,
    _roles: "admin",
  },
  {
    path: "/admin/eval-dashboard",
    exact: true,
    name: "Conversation Eval",
    component: AdminEvalDashboard,
    _roles: "admin",
  },
  {
    path: "/admin/edge-connectors",
    exact: true,
    name: "Edge Connectors",
    component: EdgeConnectorsPage,
    _roles: "admin",
    _product: "edge-connectors",
  },
  // todo: add back before pr is merged
  {
    path: "/observation-program",
    exact: true,
    name: "Observation Program",
    component: withObservationInterviewGate(ObservationProgram),
    // TODO: disabled for now, add back in future
    // adminComponent: ObservationProgramAdmin,
  },
  {
    path: "/observation-program/chat",
    exact: true,
    name: "Observation Program Chat",
    component: withObservationInterviewGate(ChatObservationPage),
  },
  {
    path: "/observation-program/chat/:id",
    exact: false,
    // name: "Observation Chat",
    component: withObservationInterviewGate(ChatObservationPage),
  },
  {
    path: "/observation-program/case",
    exact: true,
    name: "Observation Case",
    component: CaseObservation,
    _roles: "admin",
    _product: "observation-program",
  },
  {
    path: "/observation-program/activities",
    exact: true,
    name: "Activities",
    component: Activity,
    _roles: "admin",
    _product: "observation-program",
  },
  {
    path: "/oauth",
    exact: true,
    name: "OAuth Providers",
    component: OAuth,
  },
  {
    path: "/oauth/provider",
    exact: true,
    name: "Configure",
    component: OAuthConfigure,
  },
  {
    path: "/oauth/new",
    exact: true,
    name: "New",
    component: OAuthConfigure,
  },
  {
    path: "/observation-program/activities/activity-details/:id",
    exact: true,
    name: "Activity Details",
    component: () => {
      return (
        <KeyValueComponent
          useGetFunction={useGetActivityDetails}
          title={"Activity Details"}
        />
      );
    },
    _roles: "admin",
    _product: "observation-program",
  },
  {
    // criteria overview
    path: "/observation-program/criteria-overview",
    exact: true,
    name: "Criteria Overview",
    component: CriteriaOverview,
    _roles: "admin",
    _product: "observation-program",
  },
  // Demo pages (development only)
  {
    path: "/demo/design-system",
    exact: true,
    name: "Design System Demo",
    component: DesignSystemDemo,
    _roles: "admin",
  },
  {
    path: "/demo/datasets",
    exact: true,
    name: "Datasets Demo",
    component: DatasetsDemo,
    _roles: "admin",
  },
  {
    path: "/demo/model-control",
    exact: true,
    name: "Model Control Demo",
    component: ModelControlDemo,
    _roles: "admin",
  },
  {
    path: "/developer/search-eval",
    exact: true,
    name: "Search Eval",
    component: SearchEvalPage,
    _roles: "admin",
  },
  {
    path: "/developer/training-export",
    exact: true,
    name: "Training Export",
    component: TrainingExportPage,
    _roles: "admin",
  },
  {
    path: "/developer/rollouts",
    exact: true,
    name: "Rollout Generation",
    component: RolloutPage,
    _roles: "admin",
  },
  // Time Series routes
  {
    path: "/time-series",
    exact: true,
    name: "Time Series",
    component: TimeSeriesSources,
  },
  {
    path: "/time-series/:sourceId",
    exact: true,
    name: "Time Series Source Detail",
    component: TimeSeriesSourceDetail,
  },
  // Equipment AI — two live pages (hierarchy + location detail) under ONE route
  // with an array path, so the component stays mounted across dashboard ↔ asset
  // ↔ location ↔ chat. The PI sensor panel and the live SSE chat stream must
  // not remount on navigation (GHI#19699), which an array path on a single
  // <Route> guarantees and two separate routes would not. Position is
  // URL-derived: the drill lives in ?level/site/unit/system (the asset is
  // ?system), chat in ?chat_id — nothing equipment-specific is in the path.
  {
    // Standalone Integrations page (storage-only per-slot ingestion config).
    // Not in EQUIPMENT_AI_ROUTES — it mounts its own component, not the
    // pathname-driven EquipmentAI surface. Reached by URL only (no nav link).
    path: EQUIPMENT_AI_INTEGRATIONS_PATH,
    exact: true,
    name: "Equipment AI Integrations",
    component: EquipmentAIIntegrations,
    _roles: "admin",
  },
  {
    // Standalone AF-XML import page (upload → parse, like PI mode's import).
    // Not in EQUIPMENT_AI_ROUTES — it mounts its own component, not the
    // pathname-driven EquipmentAI surface. Reached by URL only (no nav link).
    path: onboardingUrl(),
    exact: true,
    name: "Equipment AI Onboarding",
    component: EquipmentAIOnboarding,
    _roles: "admin",
  },
  {
    path: EQUIPMENT_AI_ROUTES,
    exact: true,
    name: "Equipment AI",
    component: EquipmentAI,
  },
  {
    // Back-compat: the pre-cutover path-segment shapes (asset / location / chat
    // carried in the path) redirect into the URL-derived shape. The scope is in
    // the path params, so the rewrite is synchronous — no product_context
    // lookup. All exact, so they never shadow the two live routes above.
    path: [
      "/products/equipment-ai/asset/:afElementId/location/:locationId/chat/:dialogueId",
      "/products/equipment-ai/asset/:afElementId/location/:locationId",
      "/products/equipment-ai/asset/:afElementId/chat/:dialogueId",
      "/products/equipment-ai/asset/:afElementId",
      "/products/equipment-ai/location/:locationId/chat/:dialogueId",
    ],
    exact: true,
    name: "Equipment AI Legacy Redirect",
    component: EquipmentLegacyRedirect,
  },
  {
    // Naked share link — no scope in the path, so resolve the dialogue's
    // persisted product_context, then redirect to the URL-derived shape.
    path: "/products/equipment-ai/chat/:dialogueId",
    exact: true,
    name: "Equipment AI Chat Redirect",
    component: EquipmentChatRedirect,
  },
  {
    path: "/products/equipment-ai/recents",
    exact: true,
    name: "Equipment AI Chat History",
    component: EquipmentAIRecentsPage,
  },
  {
    // Scheduled analysis coverage: its own component, like Integrations,
    // reached from the header's Schedules tab.
    path: EQUIPMENT_AI_SCHEDULES_PATH,
    exact: true,
    name: "Equipment AI Schedules",
    component: EquipmentAISchedulesPage,
  },
];

export const generateLink = (routeName, params) => {
  // References the routes entries finds the matching route entry based on route name,
  // substitutes any params provided, and returns the generated path
  for (const route of routes) {
    if (route.name === routeName) {
      let newPath = route.path;
      for (const [param, value] of Object.entries(params)) {
        const regexMatchEndOfLine = new RegExp(`:${param}$`);
        const regexMatchInMiddle = new RegExp(`:${param}\\/`);
        if (regexMatchEndOfLine.test(newPath)) {
          newPath = newPath.replace(regexMatchEndOfLine, value);
        } else if (regexMatchInMiddle.test(newPath)) {
          newPath = newPath.replace(regexMatchInMiddle, `${value}/`);
        } else {
          throw `Parameter "${param}" not in matching route path ${route.path}`;
        }
      }
      return newPath;
    }
  }
  throw `Route name "${routeName}" not found`;
};

export default routes;
